/**
 * Detects transient client/network fetch errors that are recoverable on retry.
 *
 * These are browser-level fetch rejections (no HTTP status reaches us), not
 * server errors. Examples:
 *   - Chromium / Node `TypeError: Failed to fetch`
 *   - Firefox `NetworkError when attempting to fetch resource`
 *   - Safari `Load failed`
 *   - useFetch's stringified `error.value` of any of the above
 *
 * Excludes `AbortError` — caller-initiated cancellation should be treated as
 * cancellation, not a transient failure to retry.
 *
 * Matched against the *whole* message (case-insensitive), not as a substring,
 * so a hand-written `throw new Error('Operation failed to fetch records')` or
 * `throw new InternalError('Failed to fetch employer profile data', { error })`
 * is not mis-classified — only the browser's own canonical messages match.
 *
 * For wrappers like `InternalError` that preserve the inner browser error on
 * an `errorMessage` field, that field is also matched, so a wrapped
 * `TypeError: Failed to fetch` is still recognized.
 */
const KNOWN_NETWORK_MESSAGES: ReadonlySet<string> = new Set([
  'failed to fetch',
  'networkerror when attempting to fetch resource',
  'networkerror when attempting to fetch resource.',
  'load failed',
]);

// Strips the optional `TypeError:` / `Error:` prefix that useFetch leaves on
// stringified `error.value`s, so we can compare just the canonical message.
const STRINGIFIED_PREFIX_PATTERN = /^(?:typeerror|error):\s+/;

/**
 * Normalizes a message for exact-match comparison against canonical fetch
 * messages or HTTP statusTexts: lowercased, trimmed, and with useFetch's
 * stringification prefix removed. Shared so every consumer of the
 * stringification knowledge stays in sync.
 */
export function normalizeStringifiedFetchMessage(text: string): string {
  return text.toLowerCase().trim().replace(STRINGIFIED_PREFIX_PATTERN, '');
}

function matchesKnownNetworkMessage(text: string): boolean {
  return KNOWN_NETWORK_MESSAGES.has(normalizeStringifiedFetchMessage(text));
}

export function isTransientNetworkFetchError(error: unknown): boolean {
  if (error === null || error === undefined || error === '') {
    return false;
  }

  if (typeof error === 'string') {
    return matchesKnownNetworkMessage(error);
  }

  if (error instanceof Error) {
    if (error.name === 'AbortError') {
      return false;
    }

    if (matchesKnownNetworkMessage(error.message)) {
      return true;
    }

    // InternalError-style wrappers preserve the inner browser error's `.message`
    // on an `errorMessage` field. Honor it so a wrapped `Failed to fetch` is
    // still recognized while the wrapper's own conventional message is not.
    const inner = (error as { errorMessage?: unknown }).errorMessage;
    if (typeof inner === 'string' && matchesKnownNetworkMessage(inner)) {
      return true;
    }

    return false;
  }

  return false;
}
